import React, { useMemo, useCallback, useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import { Box, CircularProgress, Typography, Grid } from '@material-ui/core';
import { DataTable } from '../../../contexts/dataTable';
import { getGuestPermits } from '../../../actions/guest.permits.actions';
import GuestPermitsSearchForm from '../../../components/GuestPermitsSearch/GuestPermitsSearchForm';
import axios from 'axios';

const sortableColumns = [
  'issue_date',
  'licence_plate',
  'end_time',
  'unit_number'
];

const defaultPagination = {
  page: 1,
  page_size: 20
};

const defaultSearchParams = {
  unit_number: '',
  license_plate: '',
};

const GuestPermits = () => {
  const [pagination, setPagination] = useState(defaultPagination);
  const [searchParams, setSearchParams] = useState(defaultSearchParams);
  const [sort, setSort] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [hasSearched, setHasSearched] = useState(false);

  const { data, paging } = useSelector((state) => state.guestPermits);
  const dispatch = useDispatch();

  const cancelSearch = useRef(null);

  const columns = useMemo(
    () => [
      {
        id: 'unit_number',
        Header: 'Unit #',
        accessor: 'unit_number'
      },
      {
        id: 'issue_date',
        Header: 'Created Date',
        accessor: 'issue_date',
        Cell({ value }) {
          return moment(value).format('MM/DD/YYYY HH:mm:ss');
        }
      },
      {
        Header: 'Permit start time',
        id: 'start_time',
        accessor: 'start_time',
        Cell({ value }) {
          if (value == null || value === '') return value;
          const text = String(value);
          if (text.startsWith('No start')) return text;
          return text.split('.')[0];
        }
      },
      {
        Header: 'Permit end time',
        id: 'end_time',
        accessor: 'end_time',
        Cell({ value }) {
          if (value == null || value === '') return value;
          return String(value).split('.')[0];
        }
      },
      {
        Header: 'Guest LP',
        accessor: 'licence_plate'
      },
      {
        Header: 'Tenant Name',
        accessor: 'tenant_name'
      },
      {
        Header: 'Phone number',
        accessor: 'tenant_phone'
      }
    ],
    []
  );

  const handleClearSearch = useCallback(() => {
    setSearchParams(defaultSearchParams);
    setHasSearched(false);
    // Reset pagination when clearing search
    setPagination(defaultPagination);
  }, []);

  const getData = useCallback(() => {
    if (cancelSearch.current) {
      cancelSearch.current.cancel();
    }
    cancelSearch.current = axios.CancelToken.source();

    setIsLoading(true);

    // Build API parameters based on search fields
    const apiParams = {
      ...pagination,
      ...sort,
    };

    // Only add search parameters if they have values and a search has been performed
    if (hasSearched) {
      if (searchParams.unit_number && searchParams.unit_number.trim()) {
        apiParams.unit_number = searchParams.unit_number.trim();
      }
      if (searchParams.license_plate && searchParams.license_plate.trim()) {
        apiParams.license_plate = searchParams.license_plate.trim();
      }
    }

    dispatch(getGuestPermits(apiParams, cancelSearch.current.token)).catch((error) => {
      if (!axios.isCancel(error)) {
        throw new Error(error);
      }
    }).finally(() => setIsLoading(false));
  }, [hasSearched, sort, pagination]);

  const handleSearch = useCallback(() => {
    setHasSearched(true);
    // Reset pagination when performing new search
    setPagination(defaultPagination);
    
    // Manually trigger API call when search button is clicked
    if (cancelSearch.current) {
      cancelSearch.current.cancel();
    }
    cancelSearch.current = axios.CancelToken.source();

    const apiParams = {
      page: 1, // Reset to first page for new search
      page_size: defaultPagination.page_size,
      ...sort,
    };

    // Add search parameters if they have values
    if (searchParams.unit_number && searchParams.unit_number.trim()) {
      apiParams.unit_number = searchParams.unit_number.trim();
    }
    if (searchParams.license_plate && searchParams.license_plate.trim()) {
      apiParams.license_plate = searchParams.license_plate.trim();
    }

    setIsLoading(true);
    dispatch(getGuestPermits(apiParams, cancelSearch.current.token)).catch((error) => {
      if (!axios.isCancel(error)) {
        throw new Error(error);
      }
    }).finally(() => setIsLoading(false));
  }, [searchParams, sort]);

  // Initial load - load all guest permits without search filters
  useEffect(() => {
    if (!hasSearched) {
      getData();
    }
  }, [getData, hasSearched]);

  // Load data when pagination or sorting changes (only for searched results)
  useEffect(() => {
    if (hasSearched && (pagination.page > 1 || Object.keys(sort).length > 0)) {
      getData();
    }
  }, [getData, hasSearched, pagination, sort]);

  const isSearchActive = hasSearched && Object.values(searchParams).some(value => value && value.trim());

  return (
    <Box mr={4} pb={4}>
      <Grid container justify="space-between" alignItems="flex-start">
        <Grid item xs={12}>
          <GuestPermitsSearchForm
            searchParams={searchParams}
            onSearchParamsChange={setSearchParams}
            onSearch={handleSearch}
            onClear={handleClearSearch}
            isLoading={isLoading}
          />
        </Grid>
      </Grid>
      {isLoading ? (
        <Box
          display='flex'
          justifyContent='center'
          mt={2}
          alignItems='center'
          height='50vh'
        >
          <CircularProgress color='primary' />
        </Box>
      ) : (
        <DataTable
          data={data}
          columns={columns}
          handlePaging={setPagination}
          paging={paging}
          initialState={{
            sortBy: [
              {
                id: 'issue_date',
                desc: true
              }
            ],
            paginable: true
          }}
          sortingChanged={setSort}
          sort={sort}
          sortableColumns={sortableColumns}
          title='Guest Permits'
        />
      )}
      {!data.length && isSearchActive ? (
        <Typography variant='h5'>No results</Typography>
      ) : null}
    </Box>
  );
};

export default GuestPermits;
